import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search,
  Notifications,
  Dashboard,
  Description,
  AccountTree,
  Explore,
  Gavel,
  Analytics,
  Assessment,
  AdminPanelSettings,
  Api,
  Psychology,
  SmartToy,
  AutoAwesome,
  Warning,
  Error,
  Info,
  CheckCircle,
  GppMaybe,
  CalendarToday,
  AccountBalance,
  Class,
  People,
  Receipt,
  CalculateOutlined,
  FactCheck,
  Article,
  Shield,
  Security,
} from '@mui/icons-material';

import { useAuthStore } from '../../store/authStore';
import SidebarItem from './SidebarItem';
import { dashboardService, Alert } from '../../services/dashboardService';
import { LoggerService } from '../../services/LoggerService';

const drawerWidth = 280;

interface NavItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  section?: string;
}

const navItems: NavItem[] = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
  { text: 'Fund Explorer', icon: <Explore />, path: '/dashboard/fund-explorer' },
  { text: 'Funds', icon: <AccountTree />, path: '/dashboard/funds' },
  { text: 'Cash Positions', icon: <AccountBalance />, path: '/dashboard/cash-positions' },
  { text: 'Position Breaks', icon: <Warning />, path: '/dashboard/position-breaks' },
  { text: 'Regulatory Monitor', icon: <Gavel />, path: '/dashboard/regulatory' },
  { text: 'Regulatory Calendar', icon: <CalendarToday />, path: '/dashboard/regulatory-calendar' },
  { text: 'AI Regulatory Intelligence', icon: <AutoAwesome />, path: '/dashboard/regulatory-intelligence', section: 'AI' },
  { text: 'Risk Analysis', icon: <Analytics />, path: '/dashboard/risk-analysis' },
  { text: 'Enhanced Risk Analytics', icon: <Assessment />, path: '/dashboard/enhanced-risk-analytics', section: 'AI' },
  { text: 'Document Processing', icon: <Description />, path: '/dashboard/document-processing', section: 'AI' },
  { text: 'Fund Analytics', icon: <Analytics />, path: '/dashboard/fund-analytics' },
  { text: 'WorldCheck', icon: <GppMaybe />, path: '/dashboard/worldcheck' },
  { text: 'Reports', icon: <Assessment />, path: '/dashboard/reports' },
  { text: 'Admin Panel', icon: <AdminPanelSettings />, path: '/dashboard/admin' },
  { text: 'Data Sources', icon: <Api />, path: '/dashboard/data-sources' },
  { text: 'AI Configuration', icon: <SmartToy />, path: '/dashboard/ai-config', section: 'AI' },
  { text: 'AI/ML Config', icon: <Psychology />, path: '/dashboard/ai-ml' },
  { text: 'Share Classes', icon: <Class />, path: '/dashboard/share-classes', section: 'Fund Operations' },
  { text: 'Investors', icon: <People />, path: '/dashboard/investors', section: 'Fund Operations' },
  { text: 'Transactions', icon: <Receipt />, path: '/dashboard/transactions', section: 'Fund Operations' },
  { text: 'NAV Workflow', icon: <CalculateOutlined />, path: '/dashboard/nav-workflow', section: 'Fund Operations' },
  { text: 'Approvals', icon: <FactCheck />, path: '/dashboard/approvals', section: 'Fund Operations' },
  { text: 'KID Documents', icon: <Article />, path: '/dashboard/kid-management', section: 'Fund Operations' },
  { text: 'Depositary', icon: <Shield />, path: '/dashboard/depositary', section: 'Fund Operations' },
  { text: 'AML Screening', icon: <Security />, path: '/dashboard/aml-screening', section: 'Fund Operations' },
];

const DashboardLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<Alert[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Load notifications on component mount
  useEffect(() => {
    loadNotifications();
    // Set up interval to refresh notifications every 30 seconds
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    try {
      const alerts = await dashboardService.getAlerts(5); // Get latest 5 alerts
      setNotifications(alerts);
      setUnreadCount(alerts.filter(alert => !alert.is_read).length);
    } catch (error) {
      LoggerService.error('Error loading notifications:', error);
    }
  };

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleMarkAsRead = async (alertId: string) => {
    try {
      // Here you would typically call an API to mark alert as read
      setNotifications(prev => prev.map(alert => 
        alert.id === alertId ? { ...alert, is_read: true } : alert
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      LoggerService.error('Error marking notification as read:', error);
    }
  };

  const getNotificationIcon = (severity: string) => {
    switch (severity) {
      case 'error':
      case 'critical':
        return <Error color="error" />;
      case 'warning':
        return <Warning color="warning" />;
      case 'info':
        return <Info color="info" />;
      default:
        return <Notifications color="action" />;
    }
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    navigate('/dashboard/profile');
    handleProfileMenuClose();
  };

  const handleSettingsClick = () => {
    navigate('/dashboard/settings');
    handleProfileMenuClose();
  };

  const handleAlertsClick = () => {
    navigate('/dashboard/alerts');
    handleProfileMenuClose();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleProfileMenuClose();
  };

  // Group navigation items by section
  const groupedNavItems = navItems.reduce((groups, item) => {
    const section = item.section || 'Main';
    if (!groups[section]) {
      groups[section] = [];
    }
    groups[section].push(item);
    return groups;
  }, {} as Record<string, NavItem[]>);

  const drawer = (
    <Box>
      <Toolbar>
        <Typography variant="h6" noWrap component="div" sx={{ color: '#16a34a', fontWeight: 700 }}>
          AmudFin
        </Typography>
      </Toolbar>
      <Divider />
      <List sx={{ px: 1 }}>
        {Object.entries(groupedNavItems).map(([section, items], sectionIndex) => (
          <Box key={section}>
            {section !== 'Main' && (
              <>
                {sectionIndex > 0 && <Divider sx={{ my: 1, bgcolor: 'rgba(255,255,255,0.1)' }} />}
                <Typography
                  variant="caption"
                  sx={{
                    px: 2,
                    py: 1,
                    display: 'block',
                    color: 'rgba(255,255,255,0.7)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.75rem',
                  }}
                >
                  {section}
                </Typography>
              </>
            )}
            {items.map((item) => (
              <SidebarItem
                key={item.text}
                text={item.text}
                icon={item.icon}
                path={item.path}
                isActive={location.pathname === item.path}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) {
                    setMobileOpen(false);
                  }
                }}
              />
            ))}
          </Box>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'background.default',
          color: 'text.primary',
          borderBottom: '1px solid #1a1a1a',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <TextField
            placeholder="Search funds, regulations, or entities..."
            size="small"
            sx={{
              flexGrow: 1,
              maxWidth: 400,
              '& .MuiOutlinedInput-root': {
                borderRadius: 4,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
            <IconButton 
              color="inherit"
              onClick={handleNotificationMenuOpen}
            >
              <Badge badgeContent={unreadCount} color="error">
                <Notifications />
              </Badge>
            </IconButton>
            
            <IconButton
              onClick={handleProfileMenuOpen}
              color="inherit"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                {user?.first_name?.[0] || user?.username?.[0] || 'U'}
              </Avatar>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {user?.first_name || user?.username}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.role}
                </Typography>
              </Box>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            mt: 1.5,
            border: '1px solid #2a2a2a',
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleProfileClick}>
          <Avatar /> Profile
        </MenuItem>
        <MenuItem onClick={handleSettingsClick}>
          <Avatar /> Settings
        </MenuItem>
        <MenuItem onClick={handleAlertsClick}>
          <Avatar /> Manage Alerts
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <Avatar /> Logout
        </MenuItem>
      </Menu>

      {/* Notification Menu */}
      <Menu
        anchorEl={notificationAnchorEl}
        open={Boolean(notificationAnchorEl)}
        onClose={handleNotificationMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            mt: 1.5,
            width: 350,
            maxHeight: 400,
            border: '1px solid #2a2a2a',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Notifications
            </Typography>
            <Button
              size="small"
              onClick={() => {
                navigate('/dashboard/alerts');
                handleNotificationMenuClose();
              }}
            >
              View All
            </Button>
          </Box>
        </Box>
        
        {notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No notifications
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 300, overflow: 'auto' }}>
            {notifications.map((notification) => (
              <ListItem
                key={notification.id}
                sx={{
                  bgcolor: notification.is_read ? 'transparent' : 'action.hover',
                  borderLeft: notification.is_read ? 'none' : `3px solid ${
                    notification.severity === 'error' ? 'error.main' :
                    notification.severity === 'warning' ? 'warning.main' :
                    'info.main'
                  }`,
                }}
              >
                <ListItemIcon>
                  {getNotificationIcon(notification.severity)}
                </ListItemIcon>
                <ListItemText
                  primary={notification.title}
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {notification.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(notification.created_at).toLocaleString()}
                      </Typography>
                    </Box>
                  }
                />
                {!notification.is_read && (
                  <IconButton
                    size="small"
                    onClick={() => handleMarkAsRead(notification.id)}
                    sx={{ ml: 1 }}
                  >
                    <CheckCircle fontSize="small" />
                  </IconButton>
                )}
              </ListItem>
            ))}
          </List>
        )}
      </Menu>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: theme.custom.sidebar.backgroundColor,
              color: theme.custom.sidebar.textColor,
              '& .MuiListItemText-root': {
                color: theme.custom.sidebar.textColor,
              },
              '& .MuiListItemText-primary': {
                color: theme.custom.sidebar.textColor,
              },
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: theme.custom.sidebar.backgroundColor,
              color: theme.custom.sidebar.textColor,
              '& .MuiListItemText-root': {
                color: theme.custom.sidebar.textColor,
              },
              '& .MuiListItemText-primary': {
                color: theme.custom.sidebar.textColor,
              },
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default DashboardLayout;
